// Story 064 → TRIAGE-02 (audit decision 3.2 / IMP-02) — GLOBAL, bounded,
// in-memory undo/redo stack.
//
// History: story 064 shipped ONE stack per image, cleared on image
// switch. IMP-02 (decided 2026-06-11) replaces that with a single
// GLOBAL stack so a decision made two images back is still reachable:
//
//   - entries stay tagged with `imageId` but survive image switches,
//   - bounded at {@link UNDO_STACK_LIMIT} entries (FIFO eviction),
//   - Ctrl+Z pops the GLOBAL top; when the entry's image isn't active
//     the hotkey activates it first (so the inspector sees what is
//     being reverted) — that lives in `useUndoHotkey`,
//   - REDO: popped entries land on a redo stack (same bound);
//     Ctrl+Shift+Z replays the forward mutation. Any NEW forward push
//     clears the redo stack (standard invalidation).
//
// Entry kinds: `updateDefect`, `softDeleteDefect`,
// `createDefectFromFinding`, `dismissFinding` (TRIAGE-02 — inverse is
// POST /ai-findings/{id}/undismiss), `linkDefects` / `unlinkDefects`
// (story 029 stubs).
//
// Debounce rule (Q2 in story 064, unchanged): two consecutive
// `updateDefect` ops on the same defect + same field set within 500 ms
// collapse into one entry (`before` = first op's before, `after` =
// latest op's after).
//
// The store is intentionally pure — no API client, no React hooks. The
// wrappers in `findings/queries.ts` / `defects/queries.ts` and the
// Ctrl+Z / Ctrl+Shift+Z handler in `useUndoHotkey.ts` plug the side
// effects in.
import { create } from "zustand";
import type { Defect, DefectSeverity, DefectStatus } from "@aeriosiq/types";

/** 500 ms debounce window for same-defect-same-kind updateDefect ops. */
export const UNDO_DEBOUNCE_MS = 500;

/** IMP-02 — global stack bound; oldest entries evict FIFO. */
export const UNDO_STACK_LIMIT = 50;

export interface UpdateDefectBefore {
  status?: DefectStatus;
  /**
   * Severity story (8.0.0) — the read-side severity is nullable; `null`
   * records "was not graded" so undo descriptions stay honest. NOTE: the
   * request-side `UpdateDefectBody.severity` is still the non-nullable
   * enum, so an inverse PATCH cannot SET null — `useUndoHotkey` omits
   * null severities from the replay body (the BE derives severity from
   * the defect class anyway; the wire severity field is audit-only).
   */
  severity?: DefectSeverity | null;
  defectClassId?: string;
  catalogueLabel?: string;
}

export type UndoEntry =
  | {
      kind: "updateDefect";
      imageId: string;
      defectId: string;
      before: UpdateDefectBefore;
      after: UpdateDefectBefore;
      auditLogId: string;
      description: string;
      /** Wall-clock ms used by the debounce merge logic. */
      pushedAtMs: number;
    }
  | {
      kind: "softDeleteDefect";
      imageId: string;
      defect: Defect;
      auditLogId: string;
      description: string;
      pushedAtMs: number;
    }
  | {
      kind: "createDefectFromFinding";
      imageId: string;
      defectId: string;
      findingId: string;
      auditLogId: string;
      description: string;
      pushedAtMs: number;
    }
  | {
      // TRIAGE-02 / IMP-02 — instant, undoable dismissal. Inverse =
      // POST /ai-findings/{id}/undismiss; redo = re-dismiss (with the
      // original reason, when one was given via Shift+X).
      kind: "dismissFinding";
      imageId: string;
      findingId: string;
      reason?: string;
      auditLogId: string;
      description: string;
      pushedAtMs: number;
    }
  | {
      // Story 029 placeholder — never reached in PR-A, but typed so the
      // Ctrl+Z handler's exhaustive switch stays compiler-checked.
      kind: "linkDefects";
      imageId: string;
      linkId: string;
      auditLogId: string;
      description: string;
      pushedAtMs: number;
    }
  | {
      kind: "unlinkDefects";
      imageId: string;
      linkId: string;
      auditLogId: string;
      description: string;
      pushedAtMs: number;
    };

/**
 * Per-kind "push input" — same as {@link UndoEntry} but `pushedAtMs`
 * is optional (the store stamps Date.now() when omitted). Modeled as a
 * discriminated union per kind so TS narrows correctly on `entry.kind`.
 */
export type UndoEntryInput =
  | (Omit<Extract<UndoEntry, { kind: "updateDefect" }>, "pushedAtMs"> & {
      pushedAtMs?: number;
    })
  | (Omit<Extract<UndoEntry, { kind: "softDeleteDefect" }>, "pushedAtMs"> & {
      pushedAtMs?: number;
    })
  | (Omit<
      Extract<UndoEntry, { kind: "createDefectFromFinding" }>,
      "pushedAtMs"
    > & { pushedAtMs?: number })
  | (Omit<Extract<UndoEntry, { kind: "dismissFinding" }>, "pushedAtMs"> & {
      pushedAtMs?: number;
    })
  | (Omit<Extract<UndoEntry, { kind: "linkDefects" }>, "pushedAtMs"> & {
      pushedAtMs?: number;
    })
  | (Omit<Extract<UndoEntry, { kind: "unlinkDefects" }>, "pushedAtMs"> & {
      pushedAtMs?: number;
    });

export interface UndoStackState {
  /** The GLOBAL undo stack — LIFO array, bounded {@link UNDO_STACK_LIMIT}. */
  entries: UndoEntry[];
  /** The redo stack — fed by {@link popUndo}, cleared by {@link push}. */
  redoEntries: UndoEntry[];
  /** The currently-active image (context for the cross-image undo jump). */
  activeImageId: string | null;
  /**
   * The imperative undo/redo triggers, populated by {@link useUndoHotkey}
   * at mount. The header buttons call these so the keyboard handler +
   * the click handlers share one code path.
   */
  triggerUndo: (() => void) | null;
  triggerRedo: (() => void) | null;
  /** Forward push — merges per the debounce rule, CLEARS the redo stack. */
  push: (entry: UndoEntryInput) => void;
  /**
   * Re-install an entry onto the undo stack after a successful REDO
   * replay (or a failed undo whose state was not actually reverted).
   * No merge, does NOT clear the redo stack.
   */
  pushRestored: (entry: UndoEntry) => void;
  /** Pop the global undo top and move it onto the redo stack. */
  popUndo: () => UndoEntry | undefined;
  /** Pop the redo top. Caller `pushRestored`s it back on replay success. */
  popRedo: () => UndoEntry | undefined;
  /** Remove a specific entry from the redo stack (failed undo cleanup). */
  discardRedoEntry: (entry: UndoEntry) => void;
  peekUndo: () => UndoEntry | undefined;
  peekRedo: () => UndoEntry | undefined;
  /**
   * Track the active image. IMP-02: switching images NO LONGER clears
   * anything — the stack is global.
   */
  setActiveImage: (imageId: string | null) => void;
  setTriggerUndo: (fn: (() => void) | null) => void;
  setTriggerRedo: (fn: (() => void) | null) => void;
  /** Test-only reset; not exposed via the bridge. */
  resetAll: () => void;
}

function changedFields(
  before: UpdateDefectBefore,
  after: UpdateDefectBefore,
): Set<keyof UpdateDefectBefore> {
  const out = new Set<keyof UpdateDefectBefore>();
  for (const key of [
    "status",
    "severity",
    "defectClassId",
    "catalogueLabel",
  ] as const) {
    if (before[key] !== undefined || after[key] !== undefined) out.add(key);
  }
  return out;
}

function canMerge(
  prev: UndoEntry | undefined,
  next: UndoEntryInput,
  nowMs: number,
): prev is Extract<UndoEntry, { kind: "updateDefect" }> {
  if (!prev) return false;
  if (prev.kind !== "updateDefect") return false;
  if (next.kind !== "updateDefect") return false;
  if (prev.imageId !== next.imageId) return false;
  if (prev.defectId !== next.defectId) return false;
  if (nowMs - prev.pushedAtMs > UNDO_DEBOUNCE_MS) return false;
  // Only merge when the SAME field is being changed both times. Q2
  // in story 064: "rapidly clicks severities 2 → 3 → 4 within 200 ms".
  // Different fields (severity then status, or class then severity)
  // are independent user intents — keep them as separate stack
  // entries so each Ctrl+Z reverses one decision.
  const prevFields = changedFields(prev.before, prev.after);
  const nextFields = changedFields(next.before, next.after);
  if (prevFields.size !== nextFields.size) return false;
  for (const f of prevFields) if (!nextFields.has(f)) return false;
  return true;
}

type UpdateDefectInput = Extract<UndoEntryInput, { kind: "updateDefect" }>;

function mergedUpdateEntry(
  prev: Extract<UndoEntry, { kind: "updateDefect" }>,
  next: UpdateDefectInput,
  nowMs: number,
): UndoEntry {
  // before stays the first op's before; after rolls forward to the latest.
  return {
    kind: "updateDefect",
    imageId: prev.imageId,
    defectId: prev.defectId,
    before: prev.before,
    after: next.after,
    auditLogId: next.auditLogId, // latest forward row is the one we just learned
    description: next.description,
    pushedAtMs: nowMs,
  };
}

/** Append with the FIFO bound — drops the OLDEST entries beyond the cap. */
function bounded(entries: UndoEntry[]): UndoEntry[] {
  if (entries.length <= UNDO_STACK_LIMIT) return entries;
  return entries.slice(entries.length - UNDO_STACK_LIMIT);
}

export const useUndoStackStore = create<UndoStackState>((set, get) => ({
  entries: [],
  redoEntries: [],
  activeImageId: null,
  triggerUndo: null,
  triggerRedo: null,

  setTriggerUndo: (fn) => {
    set({ triggerUndo: fn });
  },
  setTriggerRedo: (fn) => {
    set({ triggerRedo: fn });
  },

  push: (entryInput) => {
    const nowMs = entryInput.pushedAtMs ?? Date.now();
    set((state) => {
      const top = state.entries[state.entries.length - 1];
      // Standard invalidation: a NEW forward action clears redo.
      if (
        canMerge(top, entryInput, nowMs) &&
        entryInput.kind === "updateDefect"
      ) {
        const merged = mergedUpdateEntry(top, entryInput, nowMs);
        return {
          entries: [...state.entries.slice(0, -1), merged],
          redoEntries: [],
        };
      }
      const entry: UndoEntry = { ...entryInput, pushedAtMs: nowMs };
      return {
        entries: bounded([...state.entries, entry]),
        redoEntries: [],
      };
    });
  },

  pushRestored: (entry) => {
    set((state) => ({
      entries: bounded([...state.entries, entry]),
    }));
  },

  popUndo: () => {
    const { entries } = get();
    if (entries.length === 0) return undefined;
    const entry = entries[entries.length - 1];
    if (!entry) return undefined;
    set((state) => ({
      entries: state.entries.slice(0, -1),
      redoEntries: bounded([...state.redoEntries, entry]),
    }));
    return entry;
  },

  popRedo: () => {
    const { redoEntries } = get();
    if (redoEntries.length === 0) return undefined;
    const entry = redoEntries[redoEntries.length - 1];
    if (!entry) return undefined;
    set((state) => ({
      redoEntries: state.redoEntries.slice(0, -1),
    }));
    return entry;
  },

  discardRedoEntry: (entry) => {
    set((state) => ({
      redoEntries: state.redoEntries.filter((e) => e !== entry),
    }));
  },

  peekUndo: () => {
    const { entries } = get();
    return entries[entries.length - 1];
  },
  peekRedo: () => {
    const { redoEntries } = get();
    return redoEntries[redoEntries.length - 1];
  },

  setActiveImage: (imageId) => {
    // IMP-02 — pure context tracking; the global stack survives image
    // switches (the per-image clearing of story 064 is retired).
    set({ activeImageId: imageId });
  },

  resetAll: () => {
    set({
      entries: [],
      redoEntries: [],
      activeImageId: null,
      triggerUndo: null,
      triggerRedo: null,
    });
  },
}));

/**
 * Build a human-readable description for the undo button's tooltip +
 * accessible name. Kept here so callers don't reinvent the string
 * format. The acceptance spec greps for these phrases (e.g.
 * `/severity/i`) — keep the words consistent.
 */
export function describeUndoEntry(entry: UndoEntry): string {
  switch (entry.kind) {
    case "updateDefect": {
      const parts: string[] = [];
      if (
        entry.before.severity !== undefined ||
        entry.after.severity !== undefined
      ) {
        // 8.0.0 — null is a real value ("not graded"), distinct from an
        // unknown snapshot ("?").
        const sev = (s: DefectSeverity | null | undefined): string =>
          s === null ? "not graded" : (s ?? "?");
        parts.push(
          `severity ${sev(entry.before.severity)} → ${sev(entry.after.severity)}`,
        );
      }
      if (
        entry.before.status !== undefined ||
        entry.after.status !== undefined
      ) {
        parts.push(
          `status ${entry.before.status ?? "?"} → ${entry.after.status ?? "?"}`,
        );
      }
      if (
        entry.before.catalogueLabel !== undefined ||
        entry.after.catalogueLabel !== undefined
      ) {
        parts.push(
          `class ${entry.before.catalogueLabel ?? "?"} → ${entry.after.catalogueLabel ?? "?"}`,
        );
      }
      return `change ${parts.join(", ") || "defect"}`;
    }
    case "softDeleteDefect":
      return `delete defect ${entry.defect.catalogueLabel}`;
    case "createDefectFromFinding":
      return `convert AI finding to defect`;
    case "dismissFinding":
      return `dismiss AI finding`;
    case "linkDefects":
      return `link defects`;
    case "unlinkDefects":
      return `unlink defects`;
  }
}
